// pages/sections/nlcRequestUi.js
// Shared client side of "Manager requests, Partner confirms" No Longer Client
// (server: api/shared/nlcRequests.js). Every section's NLC flow (switch -> Disengagement modal
// -> POST mark-nlc) uses these so the wording and the outcome handling stay identical:
//
//   * nlcModalProps(mode)       — modal title / intro / button for a Partner, a non-Partner, or unknown
//   * announceNlcOutcome(...)   — toast for the POST's `outcome`; returns it (the caller removes the
//                                 row only for 'marked' / 'already_nlc')
//   * requestFlagFrom(...)      — the optimistic `row.nlcRequest` after a 'requested' answer
//   * patchClientRows(...)      — apply a patch to every row of one client (job sections)
//   * withdrawNlcRequest(id)    — the requester takes a pending request back
//   * sweepRelated(...)         — the "also mark related clients" sweep, with an honest report
//
// The Partner's own list of requests to decide lives on Home (pages/NlcRequestsPanel.js).

import { toast } from 'react-toastify';
import { postJson } from './reviewUtil';
import { markRelatedParties } from './disengagementApi';

export const NLC_REQUESTS_URL = '/api/diNlcRequests';

const plural = (n, one, many) => (n === 1 ? one : many);

// mode: 'direct'  — the viewer is the client's Partner (or an unimpersonated admin): marks at once
//       'request' — the viewer is known not to be the Partner: their action files a request
//       anything else — unknown from the row (job sections); the server decides, so word it neutrally
export function nlcModalProps(mode) {
  if (mode === 'direct') {
    return { title: 'Mark as No Longer Client?', confirmLabel: 'Confirm & Mark NLC' };
  }
  if (mode === 'request') {
    return {
      title: 'Request No Longer Client',
      confirmLabel: 'Send request',
      intro: (
        <>You aren&rsquo;t this client&rsquo;s Partner, so this <strong>sends a request</strong>: the Partner must
        confirm it before the client leaves any section, and it stays on your list until then. Please answer the
        disengagement questions below so the Partner can confirm without asking you again.</>
      ),
    };
  }
  return {
    title: 'Mark NLC (a Manager’s request needs the Partner’s confirmation)',
    confirmLabel: 'Confirm',
    intro: (
      <>If you are this client&rsquo;s Partner, the client is marked No Longer Client now and excluded from all
      future data-integrity analysis. If you are its Manager (or the Job Manager on one of its jobs), this
      <strong> sends a request</strong> instead: the Partner must confirm it, and the client stays on your list
      until then. Please answer the disengagement questions below either way.</>
    ),
  };
}

// Toast for a mark-nlc answer `j` ({ outcome, partnerEmail }). Returns the normalized outcome:
// 'marked' | 'already_nlc' | 'requested' | 'already_requested'. Only the first two take the client off
// the caller's list. `markedMessage` is the section's own "marked" wording; a missing outcome is treated
// as 'marked' (the old contract).
export function announceNlcOutcome(j, clientCode, markedMessage) {
  const outcome = (j && j.outcome) || 'marked';
  const who = (j && j.partnerEmail) ? j.partnerEmail : 'the client’s Partner';
  if (outcome === 'requested') {
    toast.success(`Request sent to ${who} — ${clientCode} stays on your list until they confirm.`);
  } else if (outcome === 'already_requested') {
    toast.info(`${clientCode} already has a pending No Longer Client request${j && j.partnerEmail ? ` (waiting on ${j.partnerEmail})` : ''}.`);
  } else if (outcome === 'already_nlc') {
    toast.info(`${clientCode} was already marked No Longer Client.`);
  } else {
    toast.success(markedMessage);
  }
  return outcome;
}

// True for the outcomes that take the client out of every section.
export const nlcLeavesList = (outcome) => outcome === 'marked' || outcome === 'already_nlc';

// The optimistic `row.nlcRequest` for a just-filed request.
export function requestFlagFrom(j, email) {
  return { id: j && j.requestId != null ? j.requestId : null, requestedBy: email || '', requestedAt: new Date().toISOString(), mine: true };
}

// Patch every row of one client (the job sections list several jobs per client, and a pending
// request belongs to the client). `setData` is useSectionData's; `patch(row)` returns the new row.
export function patchClientRows(setData, contIndex, patch) {
  setData(d => (d ? { ...d, rows: (d.rows || []).map(r => (String(r.contIndex) === String(contIndex) ? patch(r) : r)) } : d));
}

// Withdraw (requester) a pending request. Throws Error(message) on failure.
export function withdrawNlcRequest(id) {
  return postJson(NLC_REQUESTS_URL, { id, action: 'withdraw' });
}

// Toasts for the related-parties sweep result { marked, requested, skipped, errors } — every
// bucket is reported (skipped / failed ones used to be silent).
export function reportRelatedSweep(r) {
  const marked = (r && r.marked) || [];
  const requested = (r && r.requested) || [];
  const skipped = (r && r.skipped) || [];
  const errors = (r && r.errors) || [];
  if (marked.length) {
    toast.success(`Also marked ${marked.length} related ${plural(marked.length, 'party', 'parties')} NLC.`);
  }
  if (requested.length) {
    toast.success(
      `Requested NLC for ${requested.length} related ${plural(requested.length, 'party', 'parties')} — `
      + `their Partners must confirm: ${requested.map(x => x.clientCode).join(', ')}.`);
  }
  if (skipped.length) {
    toast.warning(
      `${skipped.length} related ${plural(skipped.length, 'party was', 'parties were')} skipped: `
      + skipped.map(x => `${x.clientCode} (${x.reason || 'skipped'})`).join('; ') + '.',
      { autoClose: 10000 });
  }
  if (errors.length) {
    toast.error(
      `${errors.length} related ${plural(errors.length, 'party', 'parties')} failed: `
      + errors.map(x => `${x.clientCode} (${x.error || 'error'})`).join('; ') + '.',
      { autoClose: 12000 });
  }
  if (!marked.length && !requested.length && !skipped.length && !errors.length) {
    toast.info('No related parties were changed.');
  }
}

// Run the sweep for the related parties picked in the modal and report it. Never throws.
export async function sweepRelated({ disengagement, primaryContIndex, primaryClientCode, period, sourceSectionKey }) {
  const related = (disengagement && disengagement.relatedParties) || [];
  if (!related.length) return;
  try {
    reportRelatedSweep(await markRelatedParties({
      primaryContIndex, primaryClientCode, period, sourceSectionKey, disengagement, relatedParties: related,
    }));
  } catch (err) {
    toast.error(`Related parties not marked: ${err.message}`);
  }
}
